import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const FeatureButton = ({ 
  feature, 
  onClick, 
  children, 
  className = "",
  lockedClassName = "",
  showUpgradeModal = true 
}) => {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSubscriptionFeatures();
    }
  }, [user]);

  const fetchSubscriptionFeatures = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/subscription/current`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
        
        // Check if user has access to the specific feature
        const features = data?.package_config?.features || {};
        const access = checkFeatureAccess(feature, features);
        setHasAccess(access);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // Default to no access on error for paid features
      if (feature === 'ai_immigration_assistant' || feature === 'scan_uscis_notice') {
        setHasAccess(false);
      }
    }
  };

  const checkFeatureAccess = (featureName, features) => {
    switch (featureName) {
      case 'ai_immigration_assistant':
        return features.ai_immigration_assistant === true;
      case 'scan_uscis_notice':
        return features.scan_uscis_notice === true;
      case 'start_free_case_review':
        return features.start_free_case_review === true;
      default:
        return true; // Default to true for unknown features
    }
  };

  const getFeatureDisplayName = (featureName) => {
    switch (featureName) {
      case 'ai_immigration_assistant':
        return 'AI Immigration Expert';
      case 'scan_uscis_notice':
        return 'Scan My USCIS Notice';
      case 'start_free_case_review':
        return 'Start Free Case Review';
      default:
        return 'This Feature';
    }
  };

  const handleClick = () => {
    if (!user) {
      // User not logged in - show login
      onClick();
      return;
    }

    if (hasAccess) {
      // User has access - proceed normally
      onClick();
    } else {
      // User doesn't have access - show upgrade modal
      if (showUpgradeModal) {
        setShowUpgrade(true);
      }
    }
  };

  const handleUpgrade = () => {
    setShowUpgrade(false);
    window.location.href = '/pricing';
  };

  // If user is not logged in or has access, show normal button
  if (!user || hasAccess) {
    return (
      <button onClick={handleClick} className={className}>
        {children}
      </button>
    );
  }

  // User doesn't have access - show locked button
  return (
    <>
      <div className="relative">
        <button 
          onClick={handleClick}
          className={`${className} ${lockedClassName} relative opacity-75 cursor-pointer`}
        >
          {/* Lock overlay */}
          <div className="absolute inset-0 bg-black/20 rounded-xl flex items-center justify-center">
            <div className="bg-gray-800/90 rounded-lg px-3 py-1 flex items-center gap-2">
              <span className="text-yellow-400">🔒</span>
              <span className="text-white text-sm font-medium">Upgrade</span>
            </div>
          </div>
          {children}
        </button>
      </div>

      {/* Upgrade Modal */}
      {showUpgrade && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 border border-gray-600">
            {/* Lock Icon */}
            <div className="text-center mb-4">
              <div className="text-6xl mb-4">🔒</div>
              <h3 className="text-xl font-bold text-white mb-2">
                {getFeatureDisplayName(feature)} - Premium Feature
              </h3>
              <p className="text-gray-300 text-sm">
                This feature is only available on paid plans. 
                Your {subscription?.subscription?.subscription_name || 'free Explorer'} plan doesn't include this feature.
              </p>
            </div>

            {/* Upgrade Options */}
            <div className="space-y-3 mb-6">
              <div className="bg-blue-900/20 border border-blue-400 p-4 rounded-lg">
                <h4 className="text-blue-300 font-semibold mb-2">Upgrade to Navigator ($39/month)</h4>
                <ul className="text-blue-200 text-sm space-y-1">
                  <li>• ✅ AI Immigration Expert access</li>
                  <li>• ✅ Scan My USCIS Notice feature</li>
                  <li>• ✅ 200 AI interactions per month</li>
                  <li>• ✅ 15 document uploads per month</li>
                </ul>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleUpgrade}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                View Pricing Plans
              </button>
              <button
                onClick={() => setShowUpgrade(false)}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeatureButton;